import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Users, Award, Radio, GraduationCap, MapPin, DollarSign, AlertTriangle, Settings, GitBranch, FileText, Bell, Zap, Hexagon } from 'lucide-react';

function Layout({ user, onLogout, children }) {
  const location = useLocation();
  const [alerts, setAlerts] = useState([]);
  const [showAlertPanel, setShowAlertPanel] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch situational alerts
  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/alerts?active_only=true`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAlerts(data.slice(0, 5)); // Show max 5 recent alerts
        }
      } catch (error) {
        console.error('Failed to fetch alerts:', error);
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [API_URL]);

  const criticalAlerts = alerts.filter(a => a.severity === 'CRITICAL');
  const hasCritical = criticalAlerts.length > 0;

  const navigation = [
    { name: 'Command Center', path: '/', icon: Shield, section: 'main' },
    { name: 'Personnel', path: '/personnel', icon: Users, section: 'main' },
    { name: 'Promotions', path: '/promotions', icon: Award, section: 'main' },
    { name: 'Communications', path: '/communications', icon: Radio, section: 'main' },
    { name: 'Training Academy', path: '/training', icon: GraduationCap, section: 'operations' },
    { name: 'Deployments', path: '/deployments', icon: MapPin, section: 'operations' },
    { name: 'Finance', path: '/finance', icon: DollarSign, section: 'admin', restricted: true, badge: 'GFAC' },
    { name: 'Disciplinary', path: '/disciplinary', icon: AlertTriangle, section: 'admin' },
    { name: 'Admin Panel', path: '/admin', icon: Settings, section: 'admin', restricted: true, minRank: 4 },
    { name: 'Chain of Command', path: '/chain-of-command', icon: GitBranch, section: 'system' },
    { name: 'Audit Logs', path: '/audit-logs', icon: FileText, section: 'system' },
  ];

  const sections = {
    main: 'Command',
    operations: 'Operations',
    admin: 'Administration',
    system: 'System Intel'
  };

  const getRankBadgeClass = (rankName) => {
    const classes = {
      'Command': 'rank-command',
      'Commissioned Officer': 'rank-commissioned',
      'Director': 'rank-director',
      'Manager': 'rank-manager',
      'Warrant': 'rank-warrant',
      'Senior Operative': 'rank-senior-operative',
      'NCO': 'rank-nco',
      'Operative': 'rank-operative',
      'Enlisted': 'rank-enlisted',
      'Associate': 'rank-associate',
    };
    return classes[rankName] || 'rank-enlisted';
  };

  const getDoctrineBadge = () => {
    const doctrine = user.doctrine_context || user.doctrine;
    if (!doctrine) return null;
    
    const isGDT = doctrine.doctrine_type === 'GDT' || doctrine.is_gdt;
    return (
      <span className={`doctrine-badge ${isGDT ? 'doctrine-gdt' : 'doctrine-pist'}`}>
        <Hexagon size={12} />
        {isGDT ? 'GDT' : 'PIST'}
      </span>
    );
  };

  const filteredNavigation = navigation.filter(item => {
    if (item.minRank && user.rank?.level < item.minRank) return false;
    return true;
  });

  const groupedNav = Object.keys(sections).reduce((acc, section) => {
    acc[section] = filteredNavigation.filter(item => item.section === section);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-primary)' }}>
      {/* Top Navigation Bar */}
      <nav className="glass-panel border-b border-slate-800/50" style={{ borderRadius: 0 }}>
        <div className="max-w-full mx-auto px-6">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Title */}
            <div className="flex items-center space-x-2">
              <div className="relative">
                <img 
                  src="/guardian-logo.png" 
                  alt="Guardian | AEGIS" 
                  className="w-14 h-14 object-contain drop-shadow-[0_0_10px_rgba(6,182,212,0.4)]"
                />
                <div className="absolute -top-1 right-0 w-3 h-3 bg-emerald-400 rounded-full animate-pulse-cyan"></div>
              </div>
              <div>
                <h1 className="text-white font-semibold text-lg tracking-[0.15em]" style={{ fontFamily: "'Cinzel', serif", textShadow: '0 0 20px rgba(6, 182, 212, 0.5)' }}>
                  GUARDIAN | AEGIS
                </h1>
                <p className="text-xs text-cyan-400 tracking-[0.2em]" style={{ fontFamily: "'Cinzel', serif" }}>OPERATIONS COMMAND SYSTEM</p>
              </div>
            </div>

            {/* Center - Alerts Indicator */}
            <div className="flex items-center gap-4">
              <button 
                onClick={() => setShowAlertPanel(!showAlertPanel)}
                className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${hasCritical ? 'bg-red-900/30 border border-red-500/50 animate-pulse-danger' : 'bg-slate-900/50 border border-slate-700/50 hover:border-cyan-500/50'}`}
              >
                <Bell className={`w-4 h-4 ${hasCritical ? 'text-red-400' : 'text-cyan-400'}`} />
                <span className={`text-xs font-semibold ${hasCritical ? 'text-red-400' : 'text-slate-300'}`}>
                  {alerts.length} ALERTS
                </span>
                {hasCritical && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold">
                    {criticalAlerts.length}
                  </span>
                )}
              </button>
            </div>

            {/* User Info */}
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <div className="flex items-center justify-end gap-3 mb-1">
                  {getDoctrineBadge()}
                  <span className={`rank-badge ${getRankBadgeClass(user.rank?.name)}`} data-testid="user-rank">
                    {user.rank?.name}
                  </span>
                </div>
                <p className="text-white font-semibold tracking-wide" data-testid="user-full-name">{user.full_name}</p>
                <p className="text-xs text-slate-500 font-mono">{user.service_number}</p>
              </div>
              <button
                onClick={onLogout}
                className="btn-danger"
                data-testid="logout-button"
              >
                LOGOUT
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Alert Panel Dropdown */}
      {showAlertPanel && alerts.length > 0 && (
        <div className="fixed top-16 right-6 z-50 w-96 animate-slide-up">
          <div className={`alert-panel ${hasCritical ? 'has-critical' : ''}`}>
            <div className="alert-panel-header">
              <span className="text-xs font-bold tracking-wider text-slate-400">SITUATIONAL ALERTS</span>
              <span className="text-xs font-mono text-cyan-400">{alerts.length} ACTIVE</span>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {alerts.map(alert => (
                <div key={alert.id} className="alert-item">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`alert-badge ${alert.severity === 'CRITICAL' ? 'alert-critical' : alert.severity === 'WARNING' ? 'alert-warning' : 'alert-info'}`}>
                      <Zap size={10} />
                      {alert.severity}
                    </span>
                    <span className={`doctrine-badge text-[10px] ${alert.origin_doctrine?.code === 'GDT' ? 'doctrine-gdt' : 'doctrine-pist'}`} style={{ padding: '2px 6px' }}>
                      {alert.origin_doctrine?.code}
                    </span>
                  </div>
                  <p className="text-sm text-white font-medium mb-1">{alert.title}</p>
                  <p className="text-xs text-slate-400 line-clamp-2">{alert.message}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Sidebar */}
        <aside className="w-64 min-h-screen glass-panel border-r border-slate-800/50" style={{ borderRadius: 0, background: 'rgba(15, 23, 42, 0.9)' }}>
          <nav className="mt-6 px-3 space-y-6">
            {Object.entries(groupedNav).map(([sectionKey, items]) => (
              items.length > 0 && (
                <div key={sectionKey}>
                  <p className="px-3 text-[10px] font-bold uppercase tracking-[0.2em] mb-3 text-cyan-500">
                    {sections[sectionKey]}
                  </p>
                  <div className="space-y-1">
                    {items.map((item) => {
                      const isActive = location.pathname === item.path;
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 ${
                            isActive 
                              ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/10 text-cyan-400 border-l-2 border-cyan-400' 
                              : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                          }`}
                          data-testid={`nav-${item.name.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <Icon className={`mr-3 h-4 w-4 ${isActive ? 'text-cyan-400 drop-shadow-[0_0_8px_rgba(6,182,212,0.8)]' : 'text-slate-500 group-hover:text-cyan-400'}`} />
                          <span className="flex-1 tracking-wide">{item.name}</span>
                          {item.badge && (
                            <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isActive ? 'bg-cyan-500/30 text-cyan-300' : 'bg-amber-500/20 text-amber-400'}`}>
                              {item.badge}
                            </span>
                          )}
                          {item.restricted && (
                            <Shield className={`w-3 h-3 ml-1 ${isActive ? 'text-cyan-400' : 'text-amber-500/50'}`} />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              )
            ))}
          </nav>

          {/* Session Info at Bottom */}
          <div className="absolute bottom-0 w-64 p-4 border-t border-slate-800/50 bg-slate-950/50">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-500">Session</span>
              <span className="text-cyan-400 font-mono">{user.session_info?.session_timeout_minutes || 480}min</span>
            </div>
            {user.session_info?.mfa_required && (
              <div className="mt-2 px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded text-[10px] text-amber-400 text-center">
                MFA REQUIRED
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 overflow-auto hex-bg" style={{ minHeight: 'calc(100vh - 64px)' }}>
          <div className="animate-fade-in">
            {children}
          </div>
        </main>
      </div>

      {/* Click outside to close alert panel */}
      {showAlertPanel && (
        <div className="fixed inset-0 z-40" onClick={() => setShowAlertPanel(false)} />
      )}
    </div>
  );
}

export default Layout;
